import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from '../finance/shared';

const EmployeeCostCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [salary, setSalary] = useState('3.000,00');
  const [chargesRate, setChargesRate] = useState('70,00');
  const [benefits, setBenefits] = useState('600,00');
  const [resultState, setResultState] = useState<any>(null);

  const calculate = () => {
    const s = parseFloat(parseBrazilianNumber(salary)) || 0;
    const r = parseFloat(parseBrazilianNumber(chargesRate)) / 100;
    const b = parseFloat(parseBrazilianNumber(benefits)) || 0;
    if (s <= 0 || r < 0) return;

    const charges = s * r;
    const total = s + charges + b;

    const res = { salary: s, charges, benefits: b, total, rate: r * 100 };
    setResultState(res);

    onResultChange([
      { label: 'Salário', value: `R$ ${s.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Encargos', value: `R$ ${charges.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (${(r * 100).toFixed(1)}%)` },
      { label: 'Benefícios', value: `R$ ${b.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Custo Total', value: `R$ ${total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` }
    ]);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <FormField label="Salário (R$)" value={salary} onChange={setSalary} required isNumeric type="text" />
          <FormField label="Encargos (%)" value={chargesRate} onChange={setChargesRate} required isNumeric type="text" helpText="Ex.: 70%" />
          <FormField label="Benefícios (R$)" value={benefits} onChange={setBenefits} isNumeric type="text" helpText="VA/VR, plano de saúde, etc." />
        </div>

        <div className="flex justify-center mb-8">
          <button
            onClick={calculate}
            className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular Custo Funcionário
          </button>
        </div>

        {resultState && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ResultCard title="Encargos" value={`R$ ${resultState.charges.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} color="amber" />
            <ResultCard title="Benefícios" value={`R$ ${resultState.benefits.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} color="blue" />
            <ResultCard title="Custo Total" value={`R$ ${resultState.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} color="indigo" />
          </div>
        )}
      </div>

      <EducationalBlock
        title="Sobre o Custo de Funcionário"
        tips={[
          'Encargos (FGTS, INSS Patronal, etc.) podem chegar a 100% do salário nominal',
          'Considere benefícios como VA/VR, Plano de Saúde e Transporte',
          'Calcular o custo real ajuda no planejamento financeiro da empresa',
          'Varia conforme o regime tributário (Simples Nacional, Lucro Presumido, etc.)'
        ]}
      />
    </div>
  );
};

export default EmployeeCostCalculator;
